import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightLeft,
  CalendarDays,
  Check,
  ChevronDown,
  Inbox,
  MessageCircle,
  Pencil,
  Trophy,
  XCircle,
} from "lucide-react";
import { usePipelinesSettings, type PipelineStage } from "@/hooks/usePipelinesSettings";
import {
  useOpportunities,
  type DateFilterPeriod,
  type Opportunity,
  type WonOpportunityData,
} from "@/hooks/useOpportunities";
import { useAuth } from "@/contexts/AuthContext";
import { saveSelectedPipeline, getSelectedPipeline } from "@/utils/sessionStorage";
import { LossReasonDialog } from "@/components/crm/LossReasonDialog";
import { WonOpportunityDialog } from "@/components/crm/WonOpportunityDialog";
import { MobileSheet } from "../components/MobileSheet";
import { MobileOpportunityCard, formatBRL } from "../components/MobileOpportunityCard";
import { MobileOpportunityEditSheet } from "../components/MobileOpportunityEditSheet";

// Mesma chave usada pelo CRM web para compartilhar a preferência de período
const CRM_DATE_FILTER_KEY = "crm_date_filter";

const getSavedDateFilter = (accountId: string): DateFilterPeriod => {
  try {
    const saved = sessionStorage.getItem(`${CRM_DATE_FILTER_KEY}_${accountId}`);
    if (saved && ["30d", "60d", "90d", "all"].includes(saved)) {
      return saved as DateFilterPeriod;
    }
  } catch {
    // sessionStorage indisponível — usa padrão
  }
  return "30d";
};

const saveDateFilter = (accountId: string, period: DateFilterPeriod) => {
  try {
    sessionStorage.setItem(`${CRM_DATE_FILTER_KEY}_${accountId}`, period);
  } catch {
    // sessionStorage indisponível — ignora
  }
};

const DATE_FILTER_OPTIONS: { value: DateFilterPeriod; label: string }[] = [
  { value: "30d", label: "Últimos 30 dias" },
  { value: "60d", label: "Últimos 60 dias" },
  { value: "90d", label: "Últimos 90 dias" },
  { value: "all", label: "Todo o período" },
];

const stageDotColor = (stage: PipelineStage) => {
  if (stage.stage_type === "won") return "bg-success";
  if (stage.stage_type === "lost") return "bg-destructive";
  return "bg-accent-blue";
};

const MobileCrm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pipelines, stages, loading: pipelinesLoading } = usePipelinesSettings();

  const [selectedPipelineId, setSelectedPipelineId] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<DateFilterPeriod>(() =>
    user?.account_id ? getSavedDateFilter(user.account_id) : "30d"
  );
  const [activeStageIndex, setActiveStageIndex] = useState(0);

  const [pipelineSheetOpen, setPipelineSheetOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const [moveSheetOpen, setMoveSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);

  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [pendingWonData, setPendingWonData] = useState<{
    opportunityId: string;
    stageId: string;
    opportunityTitle?: string;
    initialProduct?: string;
    initialPrice?: number;
  } | null>(null);

  const [lossReasonDialogOpen, setLossReasonDialogOpen] = useState(false);
  const [pendingLossData, setPendingLossData] = useState<{
    opportunityId: string;
    stageId: string;
    opportunityTitle?: string;
  } | null>(null);

  const pagerRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  const {
    opportunities,
    loading: opportunitiesLoading,
    loadingProgress,
    moveOpportunity,
    closeOpportunity,
    reopenOpportunity,
    updateOpportunity,
  } = useOpportunities(selectedPipelineId, "all", dateFilter);

  // A oportunidade aberta é derivada da lista para refletir edições na hora
  const detailOpp = useMemo(
    () => opportunities.find((o) => o.id === selectedOpportunityId) ?? null,
    [opportunities, selectedOpportunityId]
  );

  // Restaurar funil salvo ou usar o primeiro disponível (mesma lógica do web)
  useEffect(() => {
    if (pipelines.length > 0 && !selectedPipelineId && user?.account_id) {
      const savedPipelineId = getSelectedPipeline(user.account_id);
      if (savedPipelineId && pipelines.some((p) => p.id === savedPipelineId)) {
        setSelectedPipelineId(savedPipelineId);
        return;
      }
      setSelectedPipelineId(pipelines[0].id);
      saveSelectedPipeline(user.account_id, pipelines[0].id);
    }
  }, [pipelines, selectedPipelineId, user?.account_id]);

  const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId);

  const pipelineStages = useMemo(
    () =>
      stages
        .filter((s) => s.pipeline_id === selectedPipelineId && s.is_active)
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
    [stages, selectedPipelineId]
  );

  const opportunitiesByStage = useMemo(() => {
    const map = new Map<string, Opportunity[]>();
    for (const stage of pipelineStages) map.set(stage.id, []);
    for (const opp of opportunities) {
      const list = map.get(opp.stage_id);
      if (list) list.push(opp);
    }
    for (const stage of pipelineStages) {
      const list = map.get(stage.id)!;
      if (stage.stage_type === "normal") {
        list.sort(
          (a, b) =>
            (a.order_index ?? 0) - (b.order_index ?? 0) ||
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      } else {
        list.sort(
          (a, b) =>
            new Date(b.closed_at || b.updated_at).getTime() -
            new Date(a.closed_at || a.updated_at).getTime()
        );
      }
    }
    return map;
  }, [pipelineStages, opportunities]);

  // Trocar de funil reseta a etapa ativa
  useEffect(() => {
    setActiveStageIndex(0);
    pagerRef.current?.scrollTo({ left: 0 });
  }, [selectedPipelineId]);

  // Sincroniza chip ativo com o swipe do pager
  const handlePagerScroll = useCallback(() => {
    const pager = pagerRef.current;
    if (!pager || pager.clientWidth === 0) return;
    const index = Math.round(pager.scrollLeft / pager.clientWidth);
    setActiveStageIndex((prev) => (prev === index ? prev : index));
  }, []);

  // Mantém o chip ativo visível na barra horizontal
  useEffect(() => {
    const chips = chipsRef.current;
    const activeChip = chips?.children[activeStageIndex] as HTMLElement | undefined;
    activeChip?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeStageIndex]);

  const goToStage = (index: number) => {
    setActiveStageIndex(index);
    const pager = pagerRef.current;
    pager?.scrollTo({ left: index * pager.clientWidth, behavior: "smooth" });
  };

  const handlePipelineChange = (pipelineId: string) => {
    setSelectedPipelineId(pipelineId);
    if (user?.account_id) saveSelectedPipeline(user.account_id, pipelineId);
    setPipelineSheetOpen(false);
  };

  const handleDateFilterChange = (period: DateFilterPeriod) => {
    setDateFilter(period);
    if (user?.account_id) saveDateFilter(user.account_id, period);
    setFilterSheetOpen(false);
  };

  // Mesma orquestração do CRM web: reabrir / ganhou / perdeu / mover simples
  const handleMoveToStage = async (targetStage: PipelineStage) => {
    const opportunity = detailOpp;
    if (!opportunity) return;

    setMoveSheetOpen(false);
    setSelectedOpportunityId(null);

    const isCurrentlyArchived = opportunity.status === "won" || opportunity.status === "lost";

    if (isCurrentlyArchived && targetStage.stage_type === "normal") {
      await reopenOpportunity(opportunity.id, targetStage.id);
      return;
    }

    if (targetStage.stage_type === "won") {
      setPendingWonData({
        opportunityId: opportunity.id,
        stageId: targetStage.id,
        opportunityTitle: opportunity.contact_name || "Contato sem nome",
        initialProduct: opportunity.product,
        initialPrice: opportunity.price,
      });
      setWonDialogOpen(true);
      return;
    }

    if (targetStage.stage_type === "lost") {
      setPendingLossData({
        opportunityId: opportunity.id,
        stageId: targetStage.id,
        opportunityTitle: opportunity.contact_name || "Contato sem nome",
      });
      setLossReasonDialogOpen(true);
      return;
    }

    await moveOpportunity(opportunity.id, targetStage.id);
  };

  const handleConfirmWon = async (wonData: WonOpportunityData) => {
    if (pendingWonData) {
      await closeOpportunity(pendingWonData.opportunityId, pendingWonData.stageId, "won", undefined, wonData);
      setPendingWonData(null);
    }
    setWonDialogOpen(false);
  };

  const handleConfirmLoss = async (lossReasonId: string, lostByUserId?: string) => {
    if (pendingLossData) {
      await closeOpportunity(
        pendingLossData.opportunityId,
        pendingLossData.stageId,
        "lost",
        lossReasonId,
        undefined,
        lostByUserId
      );
      setPendingLossData(null);
    }
    setLossReasonDialogOpen(false);
  };

  const handleSaveOpportunity = async (updates: Partial<Opportunity>) => {
    if (!detailOpp) return false;
    return updateOpportunity(detailOpp.id, updates);
  };

  const loading = pipelinesLoading || opportunitiesLoading;
  const currentStage = pipelineStages[activeStageIndex];
  const currentStageOpps = currentStage ? opportunitiesByStage.get(currentStage.id) || [] : [];
  const currentStageTotal = currentStageOpps.reduce((sum, o) => sum + (o.price || 0), 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Cabeçalho: seletor de funil + filtro de período */}
      <header className="shrink-0 border-b border-border bg-card px-4 pb-0 pt-3">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setPipelineSheetOpen(true)}
            className="flex min-w-0 items-center gap-1.5 rounded-lg py-1 pr-2 text-left active:bg-hover"
          >
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Funil
              </p>
              <p className="flex items-center gap-1 truncate text-lg font-bold text-foreground">
                <span className="truncate">{selectedPipeline?.name || "CRM"}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setFilterSheetOpen(true)}
            aria-label="Filtrar por período"
            className="flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-border px-3 text-[12px] font-medium text-muted-foreground active:bg-hover"
          >
            <CalendarDays className="h-4 w-4" />
            {DATE_FILTER_OPTIONS.find((o) => o.value === dateFilter)?.label.replace("Últimos ", "")}
          </button>
        </div>

        {/* Chips de etapas */}
        <div
          ref={chipsRef}
          className="crm-horizontal-scroll -mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-3"
        >
          {pipelineStages.map((stage, index) => {
            const count = opportunitiesByStage.get(stage.id)?.length || 0;
            const isActive = index === activeStageIndex;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => goToStage(index)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  isActive
                    ? "border-transparent bg-foreground text-background"
                    : "border-border bg-card text-muted-foreground active:bg-hover"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${stageDotColor(stage)}`} />
                {stage.name}
                <span
                  className={`rounded-full px-1.5 text-[11px] font-semibold ${
                    isActive ? "bg-background/20" : "bg-card-muted"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </header>

      {/* Resumo da etapa ativa */}
      {currentStage && (
        <div className="flex shrink-0 items-center justify-between border-b border-border-soft bg-background px-4 py-2">
          <p className="text-[12px] text-muted-foreground">
            {currentStageOpps.length}{" "}
            {currentStageOpps.length === 1 ? "oportunidade" : "oportunidades"}
          </p>
          <p className="text-[12px] font-semibold text-foreground">
            {formatBRL(currentStageTotal)}
          </p>
        </div>
      )}

      {loadingProgress.isLoadingMore && loadingProgress.total > 0 && (
        <div className="shrink-0 bg-background px-4 pt-2">
          <div className="h-1 overflow-hidden rounded-full bg-card-muted">
            <div
              className="h-full rounded-full bg-accent-blue transition-all duration-300"
              style={{
                width: `${Math.round((loadingProgress.loaded / loadingProgress.total) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Pager de etapas com swipe horizontal */}
      {loading && opportunities.length === 0 ? (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-card-muted" />
          ))}
        </div>
      ) : pipelineStages.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-card-muted">
            <Inbox className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-[15px] font-semibold text-foreground">Nenhum funil configurado</p>
          <p className="text-[13px] text-muted-foreground">
            Configure funis e etapas na versão web para usá-los aqui.
          </p>
        </div>
      ) : (
        <div
          ref={pagerRef}
          onScroll={handlePagerScroll}
          className="crm-horizontal-scroll flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        >
          {pipelineStages.map((stage) => {
            const stageOpps = opportunitiesByStage.get(stage.id) || [];
            return (
              <section
                key={stage.id}
                className="flex h-full w-full shrink-0 snap-center flex-col"
              >
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4 pb-6">
                  {stageOpps.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 pt-16 text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-card-muted">
                        <Inbox className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-[13px] text-muted-foreground">
                        Nenhuma oportunidade em “{stage.name}”
                      </p>
                    </div>
                  ) : (
                    stageOpps.map((opp) => (
                      <MobileOpportunityCard
                        key={opp.id}
                        opportunity={opp}
                        onClick={(o) => setSelectedOpportunityId(o.id)}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Sheet: trocar de funil */}
      <MobileSheet open={pipelineSheetOpen} onOpenChange={setPipelineSheetOpen} title="Escolher funil">
        <div className="flex flex-col gap-1 pb-2">
          {pipelines.map((pipeline) => {
            const isSelected = pipeline.id === selectedPipelineId;
            return (
              <button
                key={pipeline.id}
                type="button"
                onClick={() => handlePipelineChange(pipeline.id)}
                className="flex w-full items-center justify-between rounded-xl px-3 py-3.5 text-left transition-colors active:bg-hover"
              >
                <span
                  className={`text-[15px] ${
                    isSelected ? "font-semibold text-foreground" : "font-medium text-foreground"
                  }`}
                >
                  {pipeline.name}
                </span>
                {isSelected && <Check className="h-5 w-5 text-accent-blue" />}
              </button>
            );
          })}
        </div>
      </MobileSheet>

      {/* Sheet: período */}
      <MobileSheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen} title="Período">
        <div className="flex flex-col gap-1 pb-2">
          {DATE_FILTER_OPTIONS.map((option) => {
            const isSelected = option.value === dateFilter;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleDateFilterChange(option.value)}
                className="flex w-full items-center justify-between rounded-xl px-3 py-3.5 text-left transition-colors active:bg-hover"
              >
                <span
                  className={`text-[15px] ${
                    isSelected ? "font-semibold text-foreground" : "font-medium text-foreground"
                  }`}
                >
                  {option.label}
                </span>
                {isSelected && <Check className="h-5 w-5 text-accent-blue" />}
              </button>
            );
          })}
        </div>
      </MobileSheet>

      {/* Sheet: detalhes da oportunidade */}
      <MobileSheet
        open={!!detailOpp && !moveSheetOpen && !editSheetOpen}
        onOpenChange={(open) => {
          if (!open) setSelectedOpportunityId(null);
        }}
        title={detailOpp?.contact_name || detailOpp?.title || "Oportunidade"}
      >
        {detailOpp && (
          <div className="flex flex-col gap-4 pb-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-card-muted p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Valor
                </p>
                <p className="mt-0.5 text-[15px] font-semibold text-foreground">
                  {formatBRL(detailOpp.price)}
                </p>
              </div>
              <div className="rounded-xl bg-card-muted p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Etapa
                </p>
                <p className="mt-0.5 truncate text-[15px] font-semibold text-foreground">
                  {pipelineStages.find((s) => s.id === detailOpp.stage_id)?.name || "—"}
                </p>
              </div>
              {detailOpp.product && (
                <div className="rounded-xl bg-card-muted p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Produto
                  </p>
                  <p className="mt-0.5 truncate text-[15px] font-semibold text-foreground">
                    {detailOpp.product}
                  </p>
                </div>
              )}
              {detailOpp.contact_phone && (
                <div className="rounded-xl bg-card-muted p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Telefone
                  </p>
                  <p className="mt-0.5 truncate text-[15px] font-semibold text-foreground">
                    {detailOpp.contact_phone}
                  </p>
                </div>
              )}
              {detailOpp.attributed_user_name && (
                <div className="rounded-xl bg-card-muted p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Responsável
                  </p>
                  <p className="mt-0.5 truncate text-[15px] font-semibold text-foreground">
                    {detailOpp.attributed_user_name}
                  </p>
                </div>
              )}
              {detailOpp.status === "lost" && detailOpp.loss_reason_name && (
                <div className="rounded-xl bg-destructive/10 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-destructive">
                    Motivo da perda
                  </p>
                  <p className="mt-0.5 truncate text-[15px] font-semibold text-foreground">
                    {detailOpp.loss_reason_name}
                  </p>
                </div>
              )}
            </div>

            {detailOpp.notes && (
              <div className="rounded-xl bg-card-muted p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Anotações
                </p>
                <p className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                  {detailOpp.notes}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setEditSheetOpen(true)}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-accent-blue text-[15px] font-semibold text-white active:opacity-90"
              >
                <Pencil className="h-4 w-4" />
                Editar informações
              </button>
              <button
                type="button"
                onClick={() => setMoveSheetOpen(true)}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-border bg-card text-[15px] font-semibold text-foreground active:bg-hover"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Mover de etapa
              </button>
              {detailOpp.contact_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/m/chat/${detailOpp.contact_id}`)}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-border bg-card text-[15px] font-semibold text-foreground active:bg-hover"
                >
                  <MessageCircle className="h-4 w-4" />
                  Abrir conversa
                </button>
              )}
            </div>
          </div>
        )}
      </MobileSheet>

      {/* Sheet: mover para etapa */}
      <MobileSheet
        open={moveSheetOpen}
        onOpenChange={(open) => {
          if (!open) setMoveSheetOpen(false);
        }}
        title="Mover para"
      >
        <div className="flex flex-col gap-1 pb-2">
          {pipelineStages
            .filter((stage) => stage.id !== detailOpp?.stage_id)
            .map((stage) => (
              <button
                key={stage.id}
                type="button"
                onClick={() => handleMoveToStage(stage)}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left transition-colors active:bg-hover"
              >
                {stage.stage_type === "won" ? (
                  <Trophy className="h-5 w-5 shrink-0 text-success" />
                ) : stage.stage_type === "lost" ? (
                  <XCircle className="h-5 w-5 shrink-0 text-destructive" />
                ) : (
                  <span className={`ml-1 mr-1 h-3 w-3 shrink-0 rounded-full ${stageDotColor(stage)}`} />
                )}
                <span className="text-[15px] font-medium text-foreground">{stage.name}</span>
              </button>
            ))}
        </div>
      </MobileSheet>

      {/* Sheet: editar informações da oportunidade */}
      <MobileOpportunityEditSheet
        open={editSheetOpen}
        onOpenChange={setEditSheetOpen}
        opportunity={detailOpp}
        onSave={handleSaveOpportunity}
      />

      {/* Diálogos de ganho/perda reaproveitados do CRM web */}
      <WonOpportunityDialog
        open={wonDialogOpen}
        onClose={() => {
          setPendingWonData(null);
          setWonDialogOpen(false);
        }}
        onConfirm={handleConfirmWon}
        opportunityTitle={pendingWonData?.opportunityTitle}
        initialProduct={pendingWonData?.initialProduct}
        initialPrice={pendingWonData?.initialPrice}
      />

      <LossReasonDialog
        open={lossReasonDialogOpen}
        onClose={() => {
          setPendingLossData(null);
          setLossReasonDialogOpen(false);
        }}
        onConfirm={handleConfirmLoss}
        opportunityTitle={pendingLossData?.opportunityTitle}
      />
    </div>
  );
};

export default MobileCrm;
